import { IconLayoutDashboard } from '@developerz/ui';
import { A } from '@solidjs/router';
import { type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import type { DashboardSummary } from '../lib/dashboards';
import { orgHref } from '../lib/org';
import { StatusPill } from './StatusPill';
import { Button, buttonClass } from './ui/Button';

// One board as a row-list row (spec §3.15): icon tile + name + scope badge left,
// actions right. Presenter — the index route owns the writes.
//
// A BUILT-IN carries no rename/delete control. The API refuses both with a 409
// (they are shared, seeded state), so offering the buttons would be offering a
// guaranteed refusal; the pill says why the row is quiet instead.

export type DashboardRowProps = {
  readonly board: DashboardSummary;
  readonly onRename: (board: DashboardSummary) => void;
  readonly onDelete: (board: DashboardSummary) => void;
};

export function DashboardRow(props: DashboardRowProps): JSX.Element {
  const writable = (): boolean => !props.board.builtin;

  return (
    <li class="row-list__row">
      <div class="row-list__lead">
        <span class="row-list__tile" aria-hidden="true">
          <IconLayoutDashboard />
        </span>
        <span class="dashboards__name">{props.board.name}</span>
        <Show when={props.board.builtin}>
          <StatusPill tone="neutral">{t('dashboards.builtin')}</StatusPill>
        </Show>
      </div>
      <div class="dashboards__actions">
        <Show when={writable()}>
          <Button
            variant="ghost"
            size="sm"
            aria-label={t('dashboards.rename_named', { name: props.board.name })}
            onClick={() => props.onRename(props.board)}
          >
            {t('dashboards.rename')}
          </Button>
        </Show>
        <Show when={writable()}>
          <Button
            variant="ghost"
            size="sm"
            aria-label={t('dashboards.delete_named', { name: props.board.name })}
            onClick={() => props.onDelete(props.board)}
          >
            {t('dashboards.delete')}
          </Button>
        </Show>
        <A
          class={buttonClass({ variant: 'ghost', size: 'sm' })}
          href={orgHref(`/dashboards/${props.board.id}`)}
          aria-label={t('dashboards.open_named', { name: props.board.name })}
        >
          {t('dashboards.open_row')}
        </A>
      </div>
    </li>
  );
}
